import React, { useEffect, useState } from "react";
import axios from "axios";
import { Cloud, Droplets, Thermometer, Wind } from "lucide-react";
import ReactApexChart from "react-apexcharts";

const WeatherForecast = ({ isVisible, onClose, savedCoordinates }) => {
  const [forecast, setForecast] = useState([]);
  const [history, setHistory] = useState([]);
  const [dataforecast, setDataForecast] = useState(null);
  const [datahistory, setDataHistory] = useState(null);
  const [locationforecast, setLocationForecast] = useState(null);
  const [loading, setLoading] = useState(false);
  
  const API_KEY = "efb3685cfdd64117aa0102758242911"; // replace with your actual API key
  useEffect(() => {
    fetchWeatherForecast();
    fetchWeatherHistory();
  }, [savedCoordinates]);

  const getCoordinates = () => {
    const localStorageData = localStorage.getItem("data");
    if (localStorageData) {
      const parsedData = JSON.parse(localStorageData);
      if (parsedData.coordinates && parsedData.coordinates.length > 0) {
        return [
          parsedData.coordinates[0].latitude,
          parsedData.coordinates[0].longitude,
        ];
      }
    }
    if (savedCoordinates && savedCoordinates.length > 0) {
      return [savedCoordinates[0][1], savedCoordinates[0][0]];
    }
    return [24.8607, 67.0011];
  };

  const [lat, lon] = getCoordinates();

  const fetchWeatherForecast = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        `https://api.weatherapi.com/v1/forecast.json?key=${API_KEY}&q=${lat},${lon}&days=14`
      );
      setForecast(res.data.forecast.forecastday);
      setLocationForecast(res.data.location);
      setDataForecast(res.data.current);
      localStorage.setItem("forecast", JSON.stringify(res.data.forecast.forecastday));
    } catch (err) {
      console.error("Error fetching forecast:", err);
    } finally {
      setLoading(false);
    }
  };



const fetchWeatherHistory = async () => {
  setLoading(true);
  try {
    let sowingDateStr = localStorage.getItem("sowingDate");
    let sowingDate;
    if (!sowingDateStr) {
      const fallbackDate = new Date();
      fallbackDate.setDate(fallbackDate.getDate() - 14);
      sowingDate = fallbackDate;
    } else {
      sowingDate = new Date(sowingDateStr);
    }

    const today = new Date();
    const dayCount = Math.ceil((today - sowingDate) / (1000 * 60 * 60 * 24));

    // Prepare all promises first
    const requests = [];
    for (let i = 0; i <= dayCount; i++) {
      const current = new Date(sowingDate);
      current.setDate(sowingDate.getDate() + i);
      const formattedDate = current.toISOString().split("T")[0];

      requests.push(
        axios
          .get(
            `https://api.weatherapi.com/v1/history.json?key=${API_KEY}&q=${lat},${lon}&dt=${formattedDate}`
          )
          .then((response) => response.data?.forecast?.forecastday?.[0])
          .catch((err) => {
            console.error("Error for", formattedDate, err.message);
            return null; // don't stop others
          })
      );
    }

    // Run all requests concurrently
    const results = await Promise.all(requests);
    const validResults = results.filter(Boolean);

    if (validResults.length > 0) {
      setHistory(validResults);
      
      setDataHistory(validResults[validResults.length - 1].day);
    } else {
      console.warn("No historical data found.");
      setHistory([]);
    }
  } catch (err) {
    console.error("Error fetching history:", err);
  } finally {
    setLoading(false);
  }
};



  if (!isVisible) return null;

  const weatherDataForecast = [
    {
      icon: <Thermometer className="w-6 h-6" />,
      title: "Temperature",
      value: `${dataforecast?.temp_c ?? "N/A"}°C`,
    },
    {
      icon: <Droplets className="w-6 h-6" />,
      title: "Precipitation",
      value: `${dataforecast?.precip_mm ?? "N/A"} mm`,
    },
    {
      icon: <Wind className="w-6 h-6" />,
      title: "Wind",
      value: `${dataforecast?.wind_kph ?? "N/A"} kph`,
    },
    {
      icon: <Cloud className="w-6 h-6" />,
      title: "Cloud cover",
      value: `${dataforecast?.cloud ?? "N/A"}%`,
    },
  ];

  const commonChartOptions = (data, title) => ({
    chart: {
      toolbar: { show: true },
      background: "transparent",
      zoom: { enabled: true },
    },
    stroke: { curve: "smooth", width: 2 },
    colors: ["#FF4560", "#00E396", "#008FFB", "#775DD0", "#FEB019"],
    xaxis: {
      categories: data.map((d) => d.date),
      title: { text: "Date", style: { color: "#6b7280" } },
      labels: { rotate: -30, style: { colors: "#6b7280" } },
    },
    yaxis: {
      title: { text: "Values", style: { color: "#6b7280" } },
      labels: { style: { colors: "#6b7280" } },
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.1,
      },
    },
    tooltip: { shared: true, intersect: false },
    legend: {
      position: "top",
      horizontalAlign: "center",
      fontSize: "13px",
      labels: { colors: "#374151" },
    },
    title: { text: title, align: "center" },
  });

  const getChartSeries = (data) => [
    { name: "Max Temp (°C)", data: data.map((d) => d.day.maxtemp_c) },
    { name: "Min Temp (°C)", data: data.map((d) => d.day.mintemp_c) },
    { name: "Humidity (%)", data: data.map((d) => d.day.avghumidity) },
    { name: "Precip (mm)", data: data.map((d) => d.day.totalprecip_mm) },
    { name: "Rain (%)", data: data.map((d) => d.day.daily_chance_of_rain) },
  ];

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex items-center justify-center p-4 sm:p-6"
      style={{ backdropFilter: "blur(5px)" }}
    >
      <div className="relative bg-white w-full max-w-5xl p-4 sm:p-6 rounded-lg shadow-lg z-50 overflow-y-auto max-h-[90vh]">
        <button
          onClick={onClose}
          className="absolute w-8 h-8 sm:w-10 sm:h-10 rounded-full hover:bg-slate-200 hover:text-red-400 top-2 right-2 text-gray-600 flex items-center justify-center"
        >
          <span className="text-xl sm:text-2xl">×</span>
        </button>

        {loading ? (
          <p>Loading...</p>
        ) : (
          <>
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">Weather</h1>
            {locationforecast && (
              <p className="text-gray-600 mb-6 text-sm sm:text-base">
                {`${locationforecast.name}, ${locationforecast.region}, ${locationforecast.country}`}
              </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-8">
              {weatherDataForecast.map((item, index) => (
                <WeatherCard
                  key={index}
                  icon={item.icon}
                  title={item.title}
                  value={item.value}
                />
              ))}
            </div>

            {/* Forecast Graph */}
            <h2 className="text-xl sm:text-2xl font-bold mb-4">Forecast</h2>
            <div className="bg-gray-50 rounded-xl p-4 shadow-inner mb-10">
              <ReactApexChart
                type="area"
                height={350}
                series={getChartSeries(forecast)}
                options={commonChartOptions(forecast, "Weather Forecast Data")}
              />
            </div>

            {/* History Graph */}
            <h2 className="text-xl sm:text-2xl font-bold mb-4">History</h2>
            <div className="bg-gray-50 rounded-xl p-4 shadow-inner">
              <ReactApexChart
                type="area"
                height={350}
                series={getChartSeries(history)}
                options={commonChartOptions(history, "Historical Weather Data")}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

function WeatherCard({ icon, title, value }) {
  return (
    <div className="bg-gray-100 p-4 rounded-lg text-center">
      <div className="flex items-center justify-center mb-2">{icon}</div>
      <h3 className="text-lg font-medium mb-1">{title}</h3>
      <div className="text-2xl font-bold">{value}</div>
    </div>
  );
}

export default WeatherForecast;
